import {inject, Injectable} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_TEXTURES} from "@common/services/logging"
import {
    ContentTypeModel,
    ImageColorSpace,
    ImageDataType,
    MutationCreateTextureSetRevisionInput,
    MutationUpdateTextureSetRevisionInput,
    TextureType,
} from "@generated"
import {RefreshService} from "@app/common/services/refresh/refresh.service"
import {ImageProcessingService} from "@app/common/services/rendering/image-processing.service"
import {
    CreateMaterialAiJobAssignmentGQL,
    CreateMaterialAiJobGQL,
    CreateNewTextureSetRevisionGQL,
    DeleteScanJobGQL,
    DeleteScanSubJobGQL,
    DeleteTextureGQL,
    DeleteTextureRevisionGQL,
    DeleteTextureSetGQL,
    DeleteTextureSetRevisionGQL,
    ImageTilesDescriptorDetailsFragment,
    QueryDataObjectLegacyIdGQL,
    QueryTextureEditorDataObjectGQL,
    QueryTextureEditProcessingJobStateGQL,
    QueryTextureLoaderDataObjectImageDescriptorGQL,
    QueryTextureSetDataByLegacyIdGQL,
    QueryTextureSetDataGQL,
    QueryTextureSetDeletionDataGQL,
    QueryTextureSetReferencedMaterialInfoGQL,
    QueryTextureSetRevisionDataGQL,
    QueryTextureSetRevisionTextureRevisionIdsGQL,
    TextureEditorCancelProcessingJobGQL,
    TextureEditorCreateJobAssignmentGQL,
    TextureEditorCreateJobGQL,
    TextureEditorDataObjectFragment,
    TextureEditorUpdateTextureSetRevisionGQL,
    TexturesApiDeleteDataObjectGQL,
    TextureSetDataFragment,
    TextureSetRevisionDataFragment,
    UpdateDataObjectImageDimensionsGQL,
} from "@app/textures/service/textures-api.generated"
import {ImageProcessingNodes as Nodes, ImageProcessingNodes} from "@cm/image-processing-nodes"
import {TypedImageData} from "@cm/utils/typed-image-data"
import {fetchIgnoringErrors, fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {firstValueFrom, from} from "rxjs"
import {generateThumbnailsDataObjectTask, JobNodes, uploadProcessingMetadataTask} from "@cm/job-nodes"
import {Utility} from "@cm/job-nodes/utility"
import {imageToPbrMaps} from "@cm/job-nodes/material-ai"
import {Settings} from "@app/common/models/settings/settings"
import {graphToJson} from "@cm/utils/graph-json"
import {ImageProcessing} from "@cm/image-processing/image-processing"
import {UploadGqlService} from "@common/services/upload/upload.gql.service"
import {AsyncCacheMap} from "@common/helpers/async-cache-map/async-cache-map"
import {TextureSetRevisionData} from "@app/textures/texture-editor/texture-editor-callback"
import {getSimpleJobState, SimpleJobState} from "@app/textures/utils/simple-job-state"
import {CreateJobGraphData} from "@cm/job-nodes/job-nodes"
import {TiledImageManagerService} from "@common/services/tiled-image/tiled-image-manager.service"

export type DataObjectImageDescriptor = {
    id: string
    width: number
    height: number
    imageDataType: ImageDataType
    imageColorSpace: ImageColorSpace
    downloadUrlExr: string
    downloadUrlJpg?: string
    imageTilesDescriptor?: ImageTilesDescriptorDetailsFragment
}

export type MaterialAiJobParams = {
    textureSetRevisionId: string
    filterRadius?: number
    ignoreDpi?: boolean
    tags?: number[]
    generateDisplacement?: boolean
    inputMapType?: TextureType
}

@Injectable({providedIn: "root"})
export class TexturesApiService {
    readonly logger: CmLogger = inject(CM_LOGGER_TEXTURES)
    private readonly refreshService = inject(RefreshService)
    private readonly imageProcessingService = inject(ImageProcessingService)
    readonly uploadGqlService = inject(UploadGqlService)
    readonly tiledImageManagerService = inject(TiledImageManagerService)
    private queryTextureSetRevisionData = inject(QueryTextureSetRevisionDataGQL)
    private queryTextureSetData = inject(QueryTextureSetDataGQL)
    private queryTextureSetDataByLegacyId = inject(QueryTextureSetDataByLegacyIdGQL)
    private queryTextureSetRevisionProcessingJobStateGQL = inject(QueryTextureEditProcessingJobStateGQL)
    private queryTextureSetReferencedMaterialInfoGQL = inject(QueryTextureSetReferencedMaterialInfoGQL)
    private createNewTextureSetRevisionGQL = inject(CreateNewTextureSetRevisionGQL)
    private queryTextureEditorDataObject = inject(QueryTextureEditorDataObjectGQL)
    private queryTextureSetRevisionTextureRevisionIds = inject(QueryTextureSetRevisionTextureRevisionIdsGQL)
    private deleteTextureSetRevisionGql = inject(DeleteTextureSetRevisionGQL)
    private mutateDeleteTextureSet = inject(DeleteTextureSetGQL)
    private mutateDeleteTextureRevision = inject(DeleteTextureRevisionGQL)
    private mutateDeleteTexture = inject(DeleteTextureGQL)
    private queryTextureLoaderDataObjectImageDescriptor = inject(QueryTextureLoaderDataObjectImageDescriptorGQL)
    private updateDataObjectImageDimensions = inject(UpdateDataObjectImageDimensionsGQL)
    private queryTextureSetDeletionData = inject(QueryTextureSetDeletionDataGQL)
    private deleteScanJob = inject(DeleteScanJobGQL)
    private mutateDeleteScanSubJob = inject(DeleteScanSubJobGQL)
    private queryDataObjectLegacyIdGQL = inject(QueryDataObjectLegacyIdGQL)
    private textureEditorCancelProcessingJob = inject(TextureEditorCancelProcessingJobGQL)
    private textureEditorCreateJob = inject(TextureEditorCreateJobGQL)
    private textureEditorCreateJobAssignment = inject(TextureEditorCreateJobAssignmentGQL)
    private textureEditorUpdateTextureSetRevision = inject(TextureEditorUpdateTextureSetRevisionGQL)
    private createMaterialAiJob = inject(CreateMaterialAiJobGQL)
    private createMaterialAiJobAssignment = inject(CreateMaterialAiJobAssignmentGQL)
    private deleteDataObjectGql = inject(TexturesApiDeleteDataObjectGQL)

    private dataObjectLegacyIdCache = new AsyncCacheMap<string, number>((dataObjectId) =>
        from(
            (async () => {
                const {dataObject} = await fetchIgnoringErrors(this.queryDataObjectLegacyIdGQL)({id: dataObjectId})
                return dataObject.legacyId
            })(),
        ),
    )

    queryDataObjectLegacyId(dataObjectId: string): Promise<number> {
        return firstValueFrom(this.dataObjectLegacyIdCache.get(dataObjectId))
    }

    private textureSetRevisionCache = new AsyncCacheMap<string, TextureSetRevisionDataFragment>((textureSetRevisionId) =>
        from(
            (async () => {
                const {textureSetRevision} = await fetchIgnoringErrors(this.queryTextureSetRevisionData)({id: textureSetRevisionId})
                return textureSetRevision
            })(),
        ),
    )

    queryTextureSetRevision(textureSetRevisionId: string) {
        return firstValueFrom(this.textureSetRevisionCache.get(textureSetRevisionId))
    }

    private textureSetCache = new AsyncCacheMap<string, TextureSetDataFragment>((textureSetId) =>
        from(
            (async () => {
                const {textureSet} = await fetchIgnoringErrors(this.queryTextureSetData)({id: textureSetId})
                return textureSet
            })(),
        ),
    )

    queryTextureSet(textureSetId: string) {
        return firstValueFrom(this.textureSetCache.get(textureSetId))
    }

    async queryTextureSetByLegacyId(legacyId: number): Promise<TextureSetDataFragment> {
        const {textureSet} = await fetchIgnoringErrors(this.queryTextureSetDataByLegacyId)({legacyId})
        return textureSet
    }

    private dataObjectCache = new AsyncCacheMap<string, TextureEditorDataObjectFragment>((dataObjectId) =>
        from(
            (async () => {
                const {dataObject} = await fetchIgnoringErrors(this.queryTextureEditorDataObject)({dataObjectId: dataObjectId})
                return dataObject
            })(),
        ),
    )

    queryDataObject(dataObjectId: string) {
        return firstValueFrom(this.dataObjectCache.get(dataObjectId))
    }

    private dataObjectImageDescriptorCache = new AsyncCacheMap<string, DataObjectImageDescriptor>((dataObjectId) =>
        from(
            (async () => {
                const requireJpg = false
                const result = await fetchIgnoringErrors(this.queryTextureLoaderDataObjectImageDescriptor)({id: dataObjectId})
                let dataObject = result.dataObject

                // here we do some stunt to update potentially missing width/height of the data object
                if (!dataObject.width || !dataObject.height) {
                    // this data-object is missing width/height; try to download the image to get the dimensions
                    this.logger.warn(
                        `Data object ${dataObjectId} is missing width/height; trying to download the image to get the dimensions and update the data object`,
                    )
                    let width, height: number
                    const jpgUrl = dataObject.thumbnailJpg?.downloadUrl
                    if (jpgUrl) {
                        const image = new Image()
                        image.src = jpgUrl
                        await new Promise<void>((resolve) => {
                            image.onload = () => {
                                resolve()
                            }
                            image.onerror = (error) => {
                                throw Error(`Failed to load image: ${error}`)
                            }
                        })
                        width = image.width
                        height = image.height
                    } else {
                        const image = await this.downloadAndDecodeEXR(dataObject.downloadUrlExr)
                        width = image.width
                        height = image.height
                    }
                    // update the data object
                    await mutateThrowingErrors(this.updateDataObjectImageDimensions)({
                        dataObjectId: dataObjectId,
                        width,
                        height,
                    })
                    // re-fetch the data object
                    dataObject = await fetchThrowingErrors(this.queryTextureLoaderDataObjectImageDescriptor)({id: dataObjectId}).then(
                        (result) => result.dataObject,
                    )
                    if (!dataObject.width || !dataObject.height) {
                        throw Error(`Data object ${dataObjectId} is still missing width/height after attempting to update it`)
                    }
                }

                if (requireJpg && !dataObject.thumbnailJpg?.downloadUrl) {
                    throw Error(`Data object ${dataObjectId} is missing a downloadUrlJpg and therefore is an incomplete image descriptor.`)
                }
                const dataObjectImageDescriptor: DataObjectImageDescriptor = {
                    id: dataObject.id,
                    width: dataObject.width ?? 0,
                    height: dataObject.height ?? 0,
                    imageDataType: dataObject.imageDataType ?? ImageDataType.NonColor,
                    imageColorSpace: dataObject.imageColorSpace ?? ImageColorSpace.Linear,
                    downloadUrlExr: dataObject.downloadUrlExr,
                    downloadUrlJpg: dataObject.thumbnailJpg?.downloadUrl,
                    imageTilesDescriptor: dataObject.imageTilesDescriptor ?? undefined,
                }
                return dataObjectImageDescriptor
            })(),
        ),
    )

    queryTextureSetRevisionProcessingJobState(textureSetRevisionId: string) {
        return fetchThrowingErrors(this.queryTextureSetRevisionProcessingJobStateGQL)({textureSetRevisionId: textureSetRevisionId})
    }

    queryTextureSetReferencedMaterialInfo(materialRevisionId: string) {
        return fetchThrowingErrors(this.queryTextureSetReferencedMaterialInfoGQL)({materialRevisionId: materialRevisionId})
    }

    createNewTextureSetRevision(input: MutationCreateTextureSetRevisionInput) {
        this.textureSetCache.delete(input.textureSetId)
        return mutateThrowingErrors(this.createNewTextureSetRevisionGQL)({
            input,
        })
    }

    queryDataObjectImageDescriptor(dataObjectId: string) {
        return firstValueFrom(this.dataObjectImageDescriptorCache.get(dataObjectId))
    }

    private invalidateAllCaches() {
        this.logger.info("Invalidating all caches in TexturesApiService")
        this.textureSetCache.clear()
        this.textureSetRevisionCache.clear()
        this.dataObjectCache.clear()
        this.dataObjectImageDescriptorCache.clear()
        this.dataObjectLegacyIdCache.clear()
    }

    async deleteEmptyTextureSet(textureSetId: string) {
        this.logger.info(`Deleting empty texture set ${textureSetId}`)
        this.invalidateAllCaches()
        // very data
        const data = await fetchThrowingErrors(this.queryTextureSetDeletionData)({id: textureSetId})
        if (data.textureSet.textureSetRevisions.length > 0) {
            throw Error(`Texture set ${textureSetId} is not empty`)
        }
        // remove all old-school texture revisions
        await Promise.all(
            data.textureSet.textures
                .flatMap((texture) => texture.revisions)
                .map((revision) => mutateThrowingErrors(this.mutateDeleteTextureRevision)({id: revision.id})),
        )
        // remove all old-school textures
        await Promise.all(data.textureSet.textures.map((texture) => mutateThrowingErrors(this.mutateDeleteTexture)({id: texture.id})))
        // remove scan job along with its sub-jobs
        if (data.textureSet.scanJob) {
            // remove sub-jobs
            await Promise.all(data.textureSet.scanJob.subJobs.map((subJob) => mutateThrowingErrors(this.mutateDeleteScanSubJob)({id: subJob.id})))
            // remove scan job
            await mutateThrowingErrors(this.deleteScanJob)({id: data.textureSet.scanJob.id})
        }
        // remove texture set
        await mutateThrowingErrors(this.mutateDeleteTextureSet)({id: textureSetId})
        // refresh texture group
        this.refreshService.item({
            id: data.textureSet.textureGroup.id,
            __typename: ContentTypeModel.TextureGroup,
        })
    }

    async deleteTextureSetRevision(textureSetRevisionId: string) {
        this.logger.info(`Deleting texture set revision ${textureSetRevisionId}`)
        this.invalidateAllCaches() // we need to invalidate all caches because we the entire texture set is part of the query (not ideal !)
        const result = await fetchThrowingErrors(this.queryTextureSetRevisionTextureRevisionIds)({id: textureSetRevisionId})
        await mutateThrowingErrors(this.deleteTextureSetRevisionGql)({id: textureSetRevisionId})
        // TODO remove this work-around once the implicit creation of texture set revisions from legacy texture revisions is removed
        if (result.textureSetRevision.textureSet.textureSetRevisions.length === 1) {
            // this was the last revision of the texture set, so we need to remove all legacy texture revisions as well, otherwise a new texture set revision will be recreated from those
            this.logger.info(`Deleting all legacy textures and texture-revisions from texture-set ${result.textureSetRevision.textureSet.id}`)
            await Promise.all(
                result.textureSetRevision.textureSet.textures.flatMap((texture) =>
                    texture.revisions.map((revision) => mutateThrowingErrors(this.mutateDeleteTextureRevision)({id: revision.id})),
                ),
            )
            await Promise.all(result.textureSetRevision.textureSet.textures.map((texture) => mutateThrowingErrors(this.mutateDeleteTexture)({id: texture.id})))
        }
        this.refreshService.item({
            id: result.textureSetRevision.textureSet.id,
            __typename: ContentTypeModel.TextureSet,
        })
    }

    async deleteDataObject(id: string): Promise<void> {
        await mutateThrowingErrors(this.deleteDataObjectGql)({id})
    }

    async createBakingJob(textureSetRevision: TextureSetRevisionData, jobGraph: CreateJobGraphData): Promise<string> {
        // abort previous job if it is still running
        await this.cancelBakingJob(textureSetRevision)
        // create new job
        const result = await mutateThrowingErrors(this.textureEditorCreateJob)({
            input: {
                name: `Baking edits for texture set ${textureSetRevision.textureSet.legacyId} (revision ${textureSetRevision.id}, new system)`,
                organizationLegacyId: textureSetRevision.textureSet.textureGroup.organization.legacyId,
                graph: graphToJson(jobGraph, this.logger),
            },
        })
        const jobId = result.createJob.id
        if (jobId === undefined) {
            throw new Error("Failed to create job")
        }
        await mutateThrowingErrors(this.textureEditorCreateJobAssignment)({
            input: {
                objectId: textureSetRevision.textureSet.id,
                contentTypeModel: ContentTypeModel.TextureSet,
                jobId,
            },
        })
        this.textureSetRevisionCache.delete(textureSetRevision.id) // invalidate the cache for this texture set revision
        return jobId
    }

    async cancelBakingJob(textureSetRevision: TextureSetRevisionData) {
        const editsProcessingJob = textureSetRevision.$editsProcessingJob()
        if (editsProcessingJob && getSimpleJobState(editsProcessingJob.state) === SimpleJobState.InProgress) {
            await mutateThrowingErrors(this.textureEditorCancelProcessingJob)({jobId: editsProcessingJob.id})
        }
    }

    async updateTextureSetRevision(textureSetId: string, input: MutationUpdateTextureSetRevisionInput) {
        await mutateThrowingErrors(this.textureEditorUpdateTextureSetRevision)({
            input,
        })
        this.textureSetRevisionCache.delete(input.id) // invalidate the cache for this texture set revision
        this.textureSetCache.delete(textureSetId)
    }

    async downloadRawData(url: string, abortSignal?: AbortSignal) {
        const response = await fetch(url, {signal: abortSignal})
        if (!response.ok) {
            throw new Error(`Failed to download: ${response.status} ${response.statusText}`)
        }
        return await response.arrayBuffer()
    }

    decodeEXR(data: ArrayBuffer): Promise<TypedImageData> {
        return firstValueFrom(this.imageProcessingService.decodeEXR(new Uint8Array(data)))
    }

    async convertImage(
        image: TypedImageData,
        dataType: ImageProcessingNodes.Convert["dataType"],
        channelLayout: ImageProcessingNodes.Convert["channelLayout"],
    ): Promise<TypedImageData> {
        const convertedImage = Nodes.convert(Nodes.input(image), dataType, channelLayout, false)
        const result = await firstValueFrom(this.imageProcessingService.evalGraph(convertedImage))
        return result.image
    }

    private async downloadAndDecodeEXR(url: string): Promise<TypedImageData> {
        const data = await this.downloadRawData(url)
        const decodedImage = await this.decodeEXR(data)
        return await this.convertImage(decodedImage, "float32", "L")
    }

    evalImageProcessingGraph<T extends ImageProcessingNodes.Node>(graph: T): Promise<ImageProcessing.EvaledTypeOf<T>> {
        return firstValueFrom(this.imageProcessingService.evalGraph(graph))
    }

    async startMaterialAiJob(params: MaterialAiJobParams) {
        const inputMapType = params.inputMapType ?? TextureType.Diffuse
        const textureSetRevision = await this.queryTextureSetRevision(params.textureSetRevisionId)
        const inputDataObjectId = textureSetRevision.mapAssignments.find((mapAssignment) => mapAssignment.textureType === inputMapType)?.dataObject?.id
        const inputDataObject = inputDataObjectId ? await this.queryDataObject(inputDataObjectId) : null
        if (!inputDataObject) {
            throw Error(`Texture set revision ${params.textureSetRevisionId} does not have a map assignment for ${inputMapType}`)
        }

        const dpi: number | undefined = inputDataObject.width ? (inputDataObject.width / textureSetRevision.width) * 2.54 : undefined

        const generateTask = JobNodes.task(imageToPbrMaps, {
            input: JobNodes.value({
                inputImage: JobNodes.dataObjectReference(inputDataObject.legacyId),
                inputImageRange: 1.0,
                filterRadius: params.filterRadius,
                generateDisplacement: params.generateDisplacement,
                dpi: params.ignoreDpi ? undefined : dpi,
                tags: params.tags,
            }),
        })

        const processDataObject = (dataObject: JobNodes.TypedDataNode<JobNodes.DataObjectReference>) => {
            return JobNodes.get(
                JobNodes.task(generateThumbnailsDataObjectTask, {
                    input: JobNodes.task(uploadProcessingMetadataTask, {
                        input: JobNodes.struct({
                            dataObject,
                        }),
                    }),
                }),
                "dataObject",
            )
        }

        const mapDataObjectRefNodes = {
            diffuse: processDataObject(JobNodes.get(generateTask, "diffuse")),
            normal: processDataObject(JobNodes.get(generateTask, "normal")),
            roughness: processDataObject(JobNodes.get(generateTask, "roughness")),
            metalness: processDataObject(JobNodes.get(generateTask, "metalness")),
            anisotropy: processDataObject(JobNodes.get(generateTask, "anisotropy")),
            specularStrength: processDataObject(JobNodes.get(generateTask, "specularStrength")),
            displacement: params.generateDisplacement
                ? processDataObject(JobNodes.get(generateTask, "displacement") as JobNodes.TypedDataNode<JobNodes.DataObjectReference>)
                : undefined,
        }

        const newRevTask = JobNodes.task(Utility.TextureSetRevision.task, {
            input: JobNodes.struct({
                operation: JobNodes.value("create" as const),
                fields: JobNodes.struct({
                    textureSetId: JobNodes.value(textureSetRevision.textureSet.id),
                    name: JobNodes.value(`${textureSetRevision.name ?? "Untitled"} (Material AI)`),
                    width: JobNodes.value(textureSetRevision.width),
                    height: JobNodes.value(textureSetRevision.height),
                    // TODO: displacement scale
                    // TODO: updatedBy user?
                    createdById: JobNodes.value(textureSetRevision.createdBy ? textureSetRevision.createdBy.id : undefined),
                    mapAssignments: JobNodes.list([
                        JobNodes.struct({
                            textureType: JobNodes.value(TextureType.Diffuse),
                            dataObjectId: JobNodes.get(mapDataObjectRefNodes.diffuse, "dataObjectId"),
                        }),
                        JobNodes.struct({
                            textureType: JobNodes.value(TextureType.Normal),
                            dataObjectId: JobNodes.get(mapDataObjectRefNodes.normal, "dataObjectId"),
                        }),
                        JobNodes.struct({
                            textureType: JobNodes.value(TextureType.Roughness),
                            dataObjectId: JobNodes.get(mapDataObjectRefNodes.roughness, "dataObjectId"),
                        }),
                        JobNodes.struct({
                            textureType: JobNodes.value(TextureType.Metalness),
                            dataObjectId: JobNodes.get(mapDataObjectRefNodes.metalness, "dataObjectId"),
                        }),
                        JobNodes.struct({
                            textureType: JobNodes.value(TextureType.Anisotropy),
                            dataObjectId: JobNodes.get(mapDataObjectRefNodes.anisotropy, "dataObjectId"),
                        }),
                        JobNodes.struct({
                            textureType: JobNodes.value(TextureType.SpecularStrength),
                            dataObjectId: JobNodes.get(mapDataObjectRefNodes.specularStrength, "dataObjectId"),
                        }),
                        ...(mapDataObjectRefNodes.displacement
                            ? [
                                  JobNodes.struct({
                                      textureType: JobNodes.value(TextureType.Displacement),
                                      dataObjectId: JobNodes.get(mapDataObjectRefNodes.displacement, "dataObjectId"),
                                  }),
                              ]
                            : []),
                    ]),
                }),
            }),
        })

        const jobGraph = JobNodes.jobGraph(newRevTask, {
            progress: {
                type: "progressGroup",
                items: [
                    {
                        node: generateTask,
                        factor: 1,
                    },
                    {
                        node: newRevTask,
                        factor: 0.1,
                    },
                ],
            },
            platformVersion: Settings.APP_VERSION,
        })
        const result = await mutateThrowingErrors(this.createMaterialAiJob)({
            input: {
                name: `Material AI Job for TextureSet ${textureSetRevision.textureSet.legacyId} (revision ${textureSetRevision.id})`,
                organizationLegacyId: textureSetRevision.textureSet.textureGroup.organization.legacyId,
                graph: graphToJson(jobGraph, this.logger),
            },
        })
        const jobId = result.createJob.id
        if (jobId === undefined) {
            throw new Error("Failed to create job")
        }
        await mutateThrowingErrors(this.createMaterialAiJobAssignment)({
            input: {
                objectId: textureSetRevision.textureSet.id,
                contentTypeModel: ContentTypeModel.TextureSet,
                jobId,
            },
        })
        return jobId
    }
}
